import "./sentry-init";
import * as Sentry from "@sentry/react";

const capturedObjects = new WeakSet<object>();
const capturedPrimitives = new Set<unknown>();
let primitiveResetScheduled = false;

function hasBeenCaptured(error: unknown): boolean {
  if ((typeof error === "object" && error !== null) || typeof error === "function") {
    return capturedObjects.has(error as object);
  }
  return capturedPrimitives.has(error);
}

function markCaptured(error: unknown): void {
  if ((typeof error === "object" && error !== null) || typeof error === "function") {
    capturedObjects.add(error as object);
    return;
  }

  capturedPrimitives.add(error);
  if (!primitiveResetScheduled) {
    primitiveResetScheduled = true;
    try {
      queueMicrotask(() => {
        capturedPrimitives.clear();
        primitiveResetScheduled = false;
      });
    } catch {
      capturedPrimitives.delete(error);
      primitiveResetScheduled = false;
    }
  }
}


/**
 * Report an exception already handled by application code without attaching
 * request values, arguments, or other potentially sensitive context.
 */
export function captureHandledException(error: unknown, operation: string): void {
  try {
    const client = Sentry.getClient();
    if (!client || !Sentry.isEnabled()) return;
    if (hasBeenCaptured(error)) return;

    Sentry.withScope((scope) => {
      scope.setTag("handled_operation", operation);
      Sentry.captureException(error);
      // Mark only after SDK capture returns successfully, so transient SDK
      // failures can be retried by another handler.
      markCaptured(error);
    });
  } catch {
    // Reporting must never interfere with the application's recovery path.
  }
}
